// Identity checks waiting for a manual decision (FR-09). Details are masked.
import { Button, Group, Loader, Table, Text } from "@mantine/core";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { api, type Schemas } from "../../api/client";
import { errorMessage, unwrap } from "../../shared/session/errors";
import { useFormat } from "../../shared/ui/format";
import { FormError, Notice } from "../../shared/ui/Page";
import { AdminLayout } from "./AdminLayout";

type Item = Schemas["ReviewItem"];

export function AdminVerificationPage() {
  const { t } = useTranslation();
  return (
    <AdminLayout title={t("admin.title")}>
      <Queue />
    </AdminLayout>
  );
}

function Queue() {
  const { t } = useTranslation();
  const f = useFormat();
  const queryClient = useQueryClient();
  const queue = useQuery({
    queryKey: ["admin", "verification-queue"],
    queryFn: () => unwrap(api.GET("/admin/verification-queue", { params: { query: { limit: 100 } } })),
  });
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const items = queue.data?.items ?? [];

  const decide = async (item: Item, decision: "approve" | "reject") => {
    setError(null);
    setNotice(null);
    const { data, error } = await api.POST("/admin/verification-queue/{check_id}/decision", {
      params: { path: { check_id: item.check_id } },
      body: { decision },
    });
    if (!data) return setError(errorMessage(error, t("error.generic")));
    setNotice(t("admin_verification.decided", { name: data.masked_name, status: data.status }));
    void queryClient.invalidateQueries({ queryKey: ["admin", "verification-queue"] });
  };

  return (
    <>
      <Text size="sm" c="dimmed">
        {t("admin_verification.intro")}
      </Text>
      {notice && <Notice>{notice}</Notice>}
      <FormError message={error} />
      {queue.isPending ? (
        <Loader size="sm" aria-label={t("common.loading")} />
      ) : queue.isError ? (
        <FormError message={errorMessage(queue.error, t("error.generic"))} />
      ) : items.length === 0 ? (
        <Text c="dimmed">{t("admin_verification.empty")}</Text>
      ) : (
        <Table.ScrollContainer minWidth={760}>
          <Table striped verticalSpacing="sm">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t("admin_verification.name")}</Table.Th>
                <Table.Th>{t("admin_verification.birth_year")}</Table.Th>
                <Table.Th>{t("admin_verification.id_hint")}</Table.Th>
                <Table.Th>{t("admin_verification.provider")}</Table.Th>
                <Table.Th>{t("admin_verification.status")}</Table.Th>
                <Table.Th>{t("admin_verification.created")}</Table.Th>
                <Table.Th>{t("admin_verification.approvals")}</Table.Th>
                <Table.Th>{t("admin_companies.actions")}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {items.map((i) => (
                <Table.Tr key={i.check_id} data-testid="review-row">
                  <Table.Td>{i.masked_name}</Table.Td>
                  <Table.Td>{i.birth_year}</Table.Td>
                  <Table.Td>{i.id_number_hint}</Table.Td>
                  <Table.Td>{i.provider}</Table.Td>
                  <Table.Td>{i.status}</Table.Td>
                  <Table.Td>{f.dateTime(i.created_at)}</Table.Td>
                  <Table.Td>{i.approvals.length}</Table.Td>
                  <Table.Td>
                    <Group gap={4} wrap="nowrap">
                      <Button
                        size="xs"
                        variant="light"
                        onClick={() => void decide(i, "approve")}
                        aria-label={t("admin_verification.approve_for", { name: i.masked_name })}
                      >
                        {t("admin_verification.approve")}
                      </Button>
                      <Button
                        size="xs"
                        variant="subtle"
                        color="red"
                        onClick={() => void decide(i, "reject")}
                        aria-label={t("admin_verification.reject_for", { name: i.masked_name })}
                      >
                        {t("admin_verification.reject")}
                      </Button>
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      )}
    </>
  );
}
